/**
 * The property sidebar's Units tab — "rent roll" layout.
 *
 *   - A KPI card: total units (residential / commercial split), occupancy
 *     (leased / vacant), and in-place vs market rent with the gap broken into
 *     loss-to-lease and vacancy.
 *   - A Monthly / Annual view toggle (level 3 — presentation only; edits are
 *     always monthly, so starting an edit flips the view back to Monthly).
 *   - A trimmed units table: Unit, Type, Status, Sq ft, In-place rent (with its
 *     % vs market), Market rent, STR income, and a pinned Total row. Rows are
 *     edited in place with the pinned Save / Cancel buttons, exactly like
 *     `PropertyUnitsGrid` (`POST update_unit` changed fields only,
 *     `POST create_unit` with empties pruned).
 *
 * Type and Status are views over the model's booleans: Type over
 * `residentialUnit` / `commercialUnit`, Status over `unitVacant` (anything not
 * marked vacant reads as leased — the same rule the Summary card's occupancy
 * uses). Fields this layout doesn't show (lease type / dates, commercial use,
 * notes) are left untouched on save.
 *
 * This is an alternate to `PropertyUnitsTab` + `PropertyUnitsGrid`; the
 * sidebar picks between them with `UNITS_TAB_LAYOUT`.
 */
import { AddRounded, CheckRounded, CloseRounded, EditOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import { ActionCell, actionColumn } from "../../../screens/admin/shared/adminTableActions";
import {
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_TABLE_BOX_SX,
} from "../../../screens/LoanDrilldownScreen/shared/cardGrid";
import { amber, ink, level1, slate, status, steel } from "../../../theme/colors";
import { segmentedGroupSx } from "../../../theme/activeStates";
import AgGridTableWrapper from "../../agGrid/AgGridTableWrapper";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

const NEW_KEY = "__new";
const ROW_H = 48;

const TYPE = { RES: "Residential", COM: "Commercial", MIXED: "Mixed" };
const STATUS = { LEASED: "Leased", VACANT: "Vacant" };
const TYPE_COLOR = {
  [TYPE.RES]: steel.main,
  [TYPE.COM]: status.missing.solid,
  [TYPE.MIXED]: slate[500],
};

/* ── numbers ──────────────────────────────────────────── */

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const sum = (list, key) => list.reduce((acc, u) => acc + (num(u?.[key]) ?? 0), 0);
const money = (n) => formatDollar(n ?? 0);

/* ── unit ⇄ derived views ─────────────────────────────── */

const unitType = (u) => {
  if (u?.residentialUnit === true && u?.commercialUnit === true) return TYPE.MIXED;
  if (u?.residentialUnit === true) return TYPE.RES;
  if (u?.commercialUnit === true) return TYPE.COM;
  return "";
};
const unitStatus = (u) => (u?.unitVacant === true ? STATUS.VACANT : STATUS.LEASED);

const NUM_FIELDS = [
  "unitSquareFootage",
  "unitMonthlyInPlaceRent",
  "unitMonthlyMarketRent",
  "airbnbIncome",
];

// Inputs hold strings for numbers so a half-typed "1,2" or "" survives.
const toDraft = (u) => ({
  unitNumber: u?.unitNumber ?? "",
  type: unitType(u),
  status: unitStatus(u),
  ...Object.fromEntries(NUM_FIELDS.map((f) => [f, u?.[f] == null ? "" : String(u[f])])),
});

const TYPE_FLAGS = {
  [TYPE.RES]: { residentialUnit: true, commercialUnit: false },
  [TYPE.COM]: { residentialUnit: false, commercialUnit: true },
  [TYPE.MIXED]: { residentialUnit: true, commercialUnit: true },
  "": { residentialUnit: null, commercialUnit: null },
};

const fromDraft = (d) => ({
  unitNumber: d?.unitNumber === "" || d?.unitNumber == null ? null : d.unitNumber,
  ...TYPE_FLAGS[d?.type ?? ""],
  unitVacant: d?.status === STATUS.VACANT,
  ...Object.fromEntries(
    NUM_FIELDS.map((f) => [f, num(String(d?.[f] ?? "").replace(/[$,\s]/g, ""))]),
  ),
});

/* ── summary math ─────────────────────────────────────── */

const summarize = (units) => {
  const total = units.length;
  const vacantList = units.filter((u) => u?.unitVacant === true);
  const leasedList = units.filter((u) => u?.unitVacant !== true);
  const inPlace = sum(units, "unitMonthlyInPlaceRent");
  const market = sum(units, "unitMonthlyMarketRent");
  // Loss to lease: leased units renting under their market rent (only where
  // both numbers are on file). Vacancy: market rent of the vacant units.
  const lossToLease = leasedList.reduce((acc, u) => {
    const ip = num(u?.unitMonthlyInPlaceRent);
    const mk = num(u?.unitMonthlyMarketRent);
    return ip != null && mk != null ? acc + (mk - ip) : acc;
  }, 0);
  return {
    total,
    residential: units.filter((u) => u?.residentialUnit === true).length,
    commercial: units.filter((u) => u?.commercialUnit === true).length,
    leased: leasedList.length,
    vacant: vacantList.length,
    sqft: sum(units, "unitSquareFootage"),
    inPlace,
    market,
    str: sum(units, "airbnbIncome"),
    lossToLease,
    vacancyLoss: sum(vacantList, "unitMonthlyMarketRent"),
  };
};

/* ── KPI card pieces ──────────────────────────────────── */

const LABEL_SX = { fontSize: 12, color: slate[500] };
const BIG_SX = { fontSize: 24, fontWeight: 700, color: ink, lineHeight: 1.1, letterSpacing: "-0.02em" };

const Bar = ({ segments }) => {
  const total = segments.reduce((a, s) => a + s.value, 0);
  return (
    <Flx sx={{ height: 6, borderRadius: 3, overflow: "hidden", background: slate[200], gap: "2px" }}>
      {total > 0
        ? segments
            .filter((s) => s.value > 0)
            .map((s) => (
              <Box key={s.key} sx={{ flex: s.value, background: s.color, minWidth: 2 }} />
            ))
        : null}
    </Flx>
  );
};
Bar.propTypes = { segments: PropTypes.array.isRequired };

const Legend = ({ items }) => (
  <Flx ac wrap sx={{ gap: "4px 14px" }}>
    {items.map((it) => (
      <Flx key={it.label} ac sx={{ gap: "6px", fontSize: 12, color: slate[700] }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: it.color }} />
        {it.label}
      </Flx>
    ))}
  </Flx>
);
Legend.propTypes = { items: PropTypes.array.isRequired };

const Kpi = ({ children, wide }) => (
  <Flx
    column
    sx={{ gap: "10px", p: "18px 20px", background: "#fff", flex: wide ? "2 1 420px" : "1 1 200px", minWidth: 0 }}
  >
    {children}
  </Flx>
);
Kpi.propTypes = { children: PropTypes.node, wide: PropTypes.bool };

const Per = ({ annual }) => (
  <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: slate[500], ml: "3px", letterSpacing: 0 }}>
    {annual ? "/yr" : "/mo"}
  </Box>
);
Per.propTypes = { annual: PropTypes.bool };

const UnitKpis = ({ s, annual }) => {
  const m = annual ? 12 : 1;
  const per = annual ? "/yr" : "/mo";
  const occupancy = s.total > 0 ? Math.round((s.leased / s.total) * 100) : null;
  const gap = s.market - s.inPlace;
  const gapPct = s.market > 0 ? Math.round((Math.abs(gap) / s.market) * 100) : null;
  const other = Math.max(s.total - s.residential - s.commercial, 0);

  return (
    <Flx wrap sx={{ gap: "1px", background: slate[200], border: `1px solid ${slate[200]}`, borderRadius: "8px", overflow: "hidden" }}>
      <Kpi>
        <Txt component="div" sx={LABEL_SX}>Total units</Txt>
        <Txt component="div" sx={BIG_SX}>{s.total}</Txt>
        <Bar
          segments={[
            { key: "res", value: s.residential, color: TYPE_COLOR[TYPE.RES] },
            { key: "com", value: s.commercial, color: TYPE_COLOR[TYPE.COM] },
            { key: "other", value: other, color: slate[300] },
          ]}
        />
        <Legend
          items={[
            { label: `${s.residential} residential`, color: TYPE_COLOR[TYPE.RES] },
            { label: `${s.commercial} commercial`, color: TYPE_COLOR[TYPE.COM] },
            ...(other > 0 ? [{ label: `${other} not set`, color: slate[300] }] : []),
          ]}
        />
      </Kpi>

      <Kpi>
        <Txt component="div" sx={LABEL_SX}>Occupancy</Txt>
        <Txt component="div" sx={BIG_SX}>
          {occupancy == null ? "—" : occupancy}
          {occupancy == null ? null : (
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: slate[500], ml: "3px" }}>%</Box>
          )}
        </Txt>
        <Bar
          segments={[
            { key: "leased", value: s.leased, color: status.success.solid },
            { key: "vacant", value: s.vacant, color: slate[200] },
          ]}
        />
        <Legend
          items={[
            { label: `${s.leased} leased`, color: status.success.solid },
            { label: `${s.vacant} vacant`, color: slate[300] },
          ]}
        />
      </Kpi>

      <Kpi wide>
        <Flx wrap sx={{ gap: "10px 24px" }}>
          <Flx column sx={{ gap: "10px", flex: "1 1 160px" }}>
            <Txt component="div" sx={LABEL_SX}>In-place rent</Txt>
            <Txt component="div" sx={BIG_SX}>
              {money(s.inPlace * m)}
              <Per annual={annual} />
            </Txt>
          </Flx>
          <Flx column sx={{ gap: "10px", flex: "1 1 160px" }}>
            <Txt component="div" sx={LABEL_SX}>Market rent</Txt>
            <Txt component="div" sx={BIG_SX}>
              {money(s.market * m)}
              <Per annual={annual} />
            </Txt>
          </Flx>
        </Flx>
        <Box sx={{ height: 6, borderRadius: 3, background: slate[200], overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              width: `${s.market > 0 ? Math.min((s.inPlace / s.market) * 100, 100) : 0}%`,
              background: steel.main,
            }}
          />
        </Box>
        {s.market > 0 && gap !== 0 ? (
          <Flx ac wrap sx={{ gap: "6px 10px" }}>
            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                px: "8px",
                py: "2px",
                borderRadius: "999px",
                background: gap > 0 ? amber.light : status.success.bg,
                color: gap > 0 ? amber.chipText : status.success.fg,
              }}
            >
              {money(Math.abs(gap) * m)}
              {per} {gap > 0 ? "below" : "above"} market ({gapPct}%)
            </Box>
            {gap > 0 ? (
              <Txt component="span" sx={{ fontSize: 12, color: slate[700] }}>
                {money(s.lossToLease * m)} loss to lease, {money(s.vacancyLoss * m)} from vacancy
              </Txt>
            ) : null}
          </Flx>
        ) : null}
      </Kpi>
    </Flx>
  );
};
UnitKpis.propTypes = { s: PropTypes.object.isRequired, annual: PropTypes.bool };

/* ── table cells ──────────────────────────────────────── */

const EditCtx = createContext(null);

const INPUT_SX = {
  "& .MuiInputBase-root": { height: 32, fontSize: 13, background: "#fff" },
  "& .MuiInputBase-input": { py: 0, px: "10px" },
  "& .MuiInputAdornment-root": { mr: "-4px" },
  "& .MuiInputAdornment-root p": { fontSize: 12.5, color: slate[400] },
};

const Editor = ({ field, kind, options }) => {
  const { draft, setField, onKeyDown } = useContext(EditCtx);
  const value = draft?.[field] ?? "";
  const numeric = kind === "dollar" || kind === "number";
  return (
    <Flx ac sx={{ height: "100%", width: "100%" }}>
      <TextField
        select={kind === "select"}
        fullWidth
        size="small"
        value={value}
        placeholder={field === "unitNumber" ? "Unit" : numeric ? "0" : undefined}
        onChange={(e) => setField(field, e.target.value)}
        onKeyDown={kind === "select" ? undefined : onKeyDown}
        sx={INPUT_SX}
        inputProps={{
          inputMode: numeric ? "decimal" : undefined,
          style: numeric ? { textAlign: "right" } : undefined,
        }}
        InputProps={
          kind === "dollar"
            ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
            : undefined
        }
      >
        {kind === "select"
          ? options.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))
          : null}
      </TextField>
    </Flx>
  );
};
Editor.propTypes = { field: PropTypes.string, kind: PropTypes.string, options: PropTypes.array };

const isEditing = (ctx, p) => !p.node?.rowPinned && ctx?.editKey === p.data?.__rowKey;
const isTotal = (p) => p.node?.rowPinned === "bottom";

const UnitCell = (p) => {
  const ctx = useContext(EditCtx);
  if (isEditing(ctx, p)) return <Editor field="unitNumber" kind="text" />;
  return (
    <Txt component="span" sx={{ fontWeight: 600, color: ink, fontSize: 13 }}>
      {isTotal(p) ? "Total" : p.data?.unitNumber || "—"}
    </Txt>
  );
};

const TYPE_OPTIONS = [
  { value: TYPE.RES, label: TYPE.RES },
  { value: TYPE.COM, label: TYPE.COM },
  { value: TYPE.MIXED, label: "Residential + Commercial" },
  { value: "", label: "Not set" },
];

const TypeCell = (p) => {
  const ctx = useContext(EditCtx);
  if (isTotal(p)) return null;
  if (isEditing(ctx, p)) return <Editor field="type" kind="select" options={TYPE_OPTIONS} />;
  const t = unitType(p.data);
  if (!t) return <Box component="span" sx={{ color: slate[400] }}>—</Box>;
  return (
    <Flx ac sx={{ height: "100%", gap: "6px", fontSize: 12.5, color: slate[800] }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: TYPE_COLOR[t] }} />
      {t}
    </Flx>
  );
};

const STATUS_OPTIONS = [
  { value: STATUS.LEASED, label: STATUS.LEASED },
  { value: STATUS.VACANT, label: STATUS.VACANT },
];

const StatusCell = (p) => {
  const ctx = useContext(EditCtx);
  if (isTotal(p)) return null;
  if (isEditing(ctx, p)) return <Editor field="status" kind="select" options={STATUS_OPTIONS} />;
  const vacant = unitStatus(p.data) === STATUS.VACANT;
  const tone = vacant ? status.neutral : status.success;
  return (
    <Flx ac sx={{ height: "100%" }}>
      <Box
        component="span"
        sx={{ fontSize: 12, fontWeight: 500, lineHeight: "20px", px: "8px", borderRadius: "999px", background: tone.bg, color: tone.fg }}
      >
        {vacant ? STATUS.VACANT : STATUS.LEASED}
      </Box>
    </Flx>
  );
};

const NumberCell = (p) => {
  const ctx = useContext(EditCtx);
  const { field, kind } = p;
  if (isEditing(ctx, p)) return <Editor field={field} kind={kind} />;
  const n = num(p.data?.[field]);
  const total = isTotal(p);
  const strong = { fontWeight: total ? 700 : 500, color: ink };
  if (kind === "number") {
    if (!n && !total) return <Box component="span" sx={{ color: slate[400] }}>—</Box>;
    return <Box component="span" sx={strong}>{(n ?? 0).toLocaleString()}</Box>;
  }
  const m = ctx?.annual ? 12 : 1;
  if (!n && !total) return <Box component="span" sx={{ color: slate[400] }}>—</Box>;
  const pct = (() => {
    if (total || field !== "unitMonthlyInPlaceRent") return null;
    const mk = num(p.data?.unitMonthlyMarketRent);
    if (!n || !mk) return null;
    const d = Math.round(((n - mk) / mk) * 100);
    return d === 0 ? null : d;
  })();
  return (
    <Box component="span" sx={strong}>
      {money((n ?? 0) * m)}
      {pct != null ? (
        <Box
          component="span"
          sx={{ ml: "4px", fontSize: 10.5, fontWeight: 600, color: pct < 0 ? status.warning.fg : status.success.fg }}
        >
          {pct < 0 ? "−" : "+"}
          {Math.abs(pct)}%
        </Box>
      ) : null}
    </Box>
  );
};

const ActionsCell = (p) => {
  const { editKey, saving, startEdit, cancel, save } = useContext(EditCtx);
  if (isTotal(p)) return null;
  const rowKey = p.data?.__rowKey;
  const btnSx = { width: "100%", height: "100%", borderRadius: 0 };
  if (editKey === rowKey) {
    return (
      <ActionCell>
        <Tooltip title={rowKey === NEW_KEY ? "Create unit" : "Save unit"}>
          <span>
            <IconButton size="small" color="primary" disabled={saving} onClick={save} sx={btnSx}>
              {saving ? <CircularProgress size={14} /> : <CheckRounded fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <span>
            <IconButton size="small" disabled={saving} onClick={cancel} sx={btnSx}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ActionCell>
    );
  }
  return (
    <ActionCell>
      <Tooltip title={editKey ? "Finish the row being edited first" : "Edit unit"}>
        <span>
          <IconButton size="small" disabled={!!editKey} onClick={() => startEdit(p.data)} sx={btnSx}>
            <EditOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </ActionCell>
  );
};

/* ── columns ──────────────────────────────────────────── */

// Keys typed into a row's inputs belong to the input, not to AG Grid.
const suppressKeysInInputs = (p) => ["INPUT", "TEXTAREA"].includes(p.event?.target?.tagName);

// Unit numbers are "1", "2", "10", "B" — sort naturally; blanks last.
const naturalCompare = (a, b) => {
  const blankA = a == null || a === "";
  const blankB = b == null || b === "";
  if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

// The unsaved new row stays at the bottom whatever the sort.
const keepNewRowLast = ({ nodes }) => {
  const i = nodes.findIndex((n) => n.data?.__rowKey === NEW_KEY);
  if (i > -1) nodes.push(nodes.splice(i, 1)[0]);
};

const col = (o) => ({
  sortable: true,
  comparator: naturalCompare,
  suppressKeyboardEvent: suppressKeysInInputs,
  ...o,
});

const numCol = (field, headerName, kind, flex, minWidth) =>
  col({
    field,
    colId: field,
    headerName,
    flex,
    minWidth,
    cellRenderer: NumberCell,
    cellRendererParams: { field, kind },
    cellClass: "num",
    headerClass: "ag-right-aligned-header",
  });

const buildColumns = (annual) => {
  const sfx = annual ? " /yr" : "";
  return [
    col({ field: "unitNumber", colId: "unitNumber", headerName: "Unit", flex: 1.4, minWidth: 110, cellRenderer: UnitCell }),
    col({ colId: "type", headerName: "Type", valueGetter: (p) => unitType(p.data), flex: 1, minWidth: 130, cellRenderer: TypeCell }),
    col({ colId: "status", headerName: "Status", valueGetter: (p) => unitStatus(p.data), flex: 0.8, minWidth: 104, cellRenderer: StatusCell }),
    numCol("unitSquareFootage", "Sq ft", "number", 0.8, 96),
    numCol("unitMonthlyInPlaceRent", `In-place rent${sfx}`, "dollar", 1.3, 150),
    numCol("unitMonthlyMarketRent", `Market rent${sfx}`, "dollar", 1.1, 130),
    numCol("airbnbIncome", `STR income${sfx}`, "dollar", 1.1, 130),
    actionColumn({ colId: "actions", count: 2, cellRenderer: ActionsCell }),
  ];
};

const GRID_SX = {
  ...CARD_GRID_SX,
  "--ag-cell-horizontal-padding": "14px",
  "--ag-row-height": `${ROW_H}px`,
  "--ag-header-column-resize-handle-display": "none",
  // Rows are taller than the admin grid's 36px line box — centre every cell
  // vertically (numbers stay right-aligned).
  "& .ag-cell:not(.act)": { display: "flex", alignItems: "center" },
  "& .ag-cell.num": { justifyContent: "flex-end" },
  "& .unit-row-editing": { background: level1.hoverTint },
  // Total row: a hairline above, a faint wash, and sans figures like the
  // rest of the table's emphasised text.
  "& .ag-floating-bottom": { borderTop: `1px solid ${slate[200]}`, background: slate[50] },
  "& .ag-floating-bottom .ag-row": { background: slate[50], borderBottom: "none" },
  "& .ag-floating-bottom .ag-cell, & .ag-floating-bottom .ag-cell.num": {
    fontFamily: "inherit",
    fontWeight: 700,
    color: ink,
  },
};

/* ── the tab ──────────────────────────────────────────── */

const PropertyUnitsSummaryTab = ({ property, units, loanId, createRequested, onCreateHandled }) => {
  const dispatch = useDispatch();
  const { createSubjectPropertyUnit, updateSubjectPropertyUnit } = useUnderwritingHook();
  const propertyId = property?._id;
  const list = useMemo(() => units || [], [units]);
  const s = useMemo(() => summarize(list), [list]);

  const [period, setPeriod] = useState("monthly");
  const annual = period === "annual";
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // A different property never inherits a half-edited row.
  useEffect(() => {
    setEditKey(null);
    setDraft(null);
  }, [propertyId]);

  // Inputs are monthly — never edit against an annualized view.
  const startAdd = useCallback(() => {
    setPeriod("monthly");
    setEditKey(NEW_KEY);
    setDraft(toDraft({ residentialUnit: true, unitVacant: false }));
  }, []);

  useEffect(() => {
    if (!createRequested) return;
    startAdd();
    onCreateHandled?.();
  }, [createRequested, startAdd, onCreateHandled]);

  const rows = useMemo(() => {
    const r = list.map((u, i) => ({ ...u, __rowKey: String(u?._id || `idx-${i}`) }));
    if (editKey === NEW_KEY) r.push({ __rowKey: NEW_KEY });
    return r;
  }, [list, editKey]);

  const totalRow = useMemo(
    () => [
      {
        __rowKey: "__total",
        unitSquareFootage: s.sqft,
        unitMonthlyInPlaceRent: s.inPlace,
        unitMonthlyMarketRent: s.market,
        airbnbIncome: s.str,
      },
    ],
    [s],
  );

  const startEdit = useCallback((row) => {
    setPeriod("monthly");
    setEditKey(row.__rowKey);
    setDraft(toDraft(row));
  }, []);

  const cancel = useCallback(() => {
    setEditKey(null);
    setDraft(null);
  }, []);

  const setField = useCallback((field, value) => setDraft((d) => ({ ...d, [field]: value })), []);

  const onSaved = useCallback(
    (response) => {
      dispatch(loanDrilldownSet(response));
      dispatch(loanPipelineClear(response));
      setEditKey(null);
      setDraft(null);
    },
    [dispatch],
  );

  const save = useCallback(() => {
    if (!editKey || !draft || saving) return;
    const next = fromDraft(draft);

    if (editKey === NEW_KEY) {
      const body = Object.fromEntries(Object.entries(next).filter(([, v]) => v !== null));
      setSaving(true);
      createSubjectPropertyUnit({
        loanId,
        propertyId,
        propertyData: body,
        onSuccessMsg: "Unit added",
        onSuccessFn: onSaved,
        onCompleteFn: () => setSaving(false),
      });
      return;
    }

    // Only the fields that moved, compared in the same normalised form — so an
    // untouched unit with no Type / Status on file isn't written.
    const row = rows.find((r) => r.__rowKey === editKey);
    const before = fromDraft(toDraft(row));
    const changed = Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== before[k]));
    if (!Object.keys(changed).length || !row?._id) {
      cancel();
      return;
    }
    setSaving(true);
    updateSubjectPropertyUnit({
      loanId,
      propertyId,
      unitId: row._id,
      unitData: changed,
      onSuccessMsg: "Unit saved",
      onSuccessFn: onSaved,
      onCompleteFn: () => setSaving(false),
    });
  }, [editKey, draft, saving, rows, loanId, propertyId, createSubjectPropertyUnit, updateSubjectPropertyUnit, onSaved, cancel]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        save();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  const ctx = useMemo(
    () => ({ editKey, draft, setField, saving, startEdit, cancel, save, onKeyDown, annual }),
    [editKey, draft, setField, saving, startEdit, cancel, save, onKeyDown, annual],
  );

  const columns = useMemo(() => buildColumns(annual), [annual]);
  const rowClassRules = useMemo(
    () => ({ "unit-row-editing": (p) => !p.node?.rowPinned && p.data?.__rowKey === editKey }),
    [editKey],
  );

  return (
    <EditCtx.Provider value={ctx}>
      <Flx column sx={{ gap: "28px", p: "20px 16px 24px" }}>
        <Flx column sx={{ gap: "12px" }}>
          <Flx ac jb wrap sx={{ gap: "8px 16px" }}>
            <Flx ac sx={{ gap: "10px", minWidth: 0 }}>
              <Txt component="span" sx={{ fontSize: 15, fontWeight: 600, color: ink }}>
                Unit summary
              </Txt>
              {s.sqft > 0 ? (
                <Txt component="span" sx={{ fontSize: 12.5, color: slate[500] }}>
                  {s.sqft.toLocaleString()} sq ft total
                </Txt>
              ) : null}
            </Flx>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={period}
              disabled={!!editKey}
              onChange={(_, v) => v && setPeriod(v)}
              // The default track is nearly the page's gray — on this
              // background it needs a darker fill and a hairline to read.
              sx={{
                ...segmentedGroupSx(),
                backgroundColor: slate[200],
                border: `1px solid ${slate[300]}`,
              }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annual">Annual</ToggleButton>
            </ToggleButtonGroup>
          </Flx>
          <UnitKpis s={s} annual={annual} />
        </Flx>

        <Flx column sx={{ gap: "12px" }}>
          <Flx ac jb sx={{ gap: "16px" }}>
            <Txt component="span" sx={{ fontSize: 15, fontWeight: 600, color: ink }}>
              Units
            </Txt>
            <Button
              size="small"
              variant="contained"
              disabled={!!editKey}
              startIcon={<AddRounded sx={{ fontSize: "18px !important" }} />}
              onClick={startAdd}
              sx={{ flexShrink: 0 }}
            >
              Add unit
            </Button>
          </Flx>
          {rows.length ? (
            <Box sx={{ ...CARD_TABLE_BOX_SX, background: "#fff" }}>
              <AgGridTableWrapper
                compactTable
                noBorder
                suppressMinHeightWhenPopulated={rows}
                sx={GRID_SX}
              >
                <AgGridReact
                  rowData={rows}
                  pinnedBottomRowData={totalRow}
                  columnDefs={columns}
                  defaultColDef={CARD_GRID_COL_DEF}
                  getRowId={(p) => p.data.__rowKey}
                  rowClassRules={rowClassRules}
                  postSortRows={keepNewRowLast}
                  rowHeight={ROW_H}
                  domLayout="autoHeight"
                  suppressCellFocus
                />
              </AgGridTableWrapper>
            </Box>
          ) : (
            <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
              No units added yet.
            </Txt>
          )}
        </Flx>
      </Flx>
    </EditCtx.Provider>
  );
};

PropertyUnitsSummaryTab.propTypes = {
  property: PropTypes.object,
  units: PropTypes.array,
  loanId: PropTypes.string,
  createRequested: PropTypes.bool,
  onCreateHandled: PropTypes.func,
};

export default PropertyUnitsSummaryTab;
